//src/pages/MyOrders.jsx
import { useState, useEffect, useCallback } from "react";
import {
  Package, RefreshCw, Copy, CheckCheck, Search,
  ShoppingBag, ChevronDown, ChevronUp, X, Filter,
} from "lucide-react";
import api from "../api/api";
import { toast } from "react-toastify";
import Currency from "../components/Currency";

// ── Category icons (emoji fallback — lightweight, no extra imports) ────────────
const CATEGORY_EMOJI = {
  facebook:    "🔵",
  instagram:   "🟣",
  tiktok:      "⚫",
  twitter:     "🐦",
  "twitter/x": "🐦",
  x:           "🐦",
  telegram:    "✈️",
  vpn:         "🔒",
  texting_app: "💬",
  google:      "🔍",
  yahoo:       "🟣",
  outlook:     "📧",
  hotmail:     "📧",
  whatsapp:    "💚",
  other:       "📱",
};

const CATEGORY_LABEL = {
  facebook:    "Facebook",
  instagram:   "Instagram",
  tiktok:      "TikTok",
  twitter:     "Twitter/X",
  "twitter/x": "Twitter/X",
  x:           "Twitter/X",
  telegram:    "Telegram",
  vpn:         "VPN",
  texting_app: "Texting App",
  google:      "Google",
  yahoo:       "Yahoo",
  outlook:     "Outlook",
  hotmail:     "Hotmail",
  whatsapp:    "WhatsApp",
  other:       "Other",
};


function buildCredentialLine(credentials, previewUrl) {
  if (!credentials) return previewUrl || "No credentials available";

  // VaultAcc + Sujan: full content in credentials.email, nothing else set
  if (credentials.email && !credentials.password && !credentials.extra) {
    return previewUrl ? `${credentials.email}\n${previewUrl}` : credentials.email;
  }

  // Manual DB: assemble from fields
  const parts = [];
  if (credentials.email)    parts.push(credentials.email);
  if (credentials.password) parts.push(credentials.password);
  if (credentials.extra)    parts.push(credentials.extra);
  if (previewUrl)           parts.push(previewUrl);
  return parts.length > 0 ? parts.join(":") : "No credentials available";
}

function formatDate(dateStr) {
  return new Date(dateStr).toLocaleDateString("en-NG", {
    day:   "numeric",
    month: "short",
    year:  "numeric",
  });
}

// ── Single Order Card ─────────────────────────────────────────────────────────
function OrderCard({ purchase, index }) {
  const [expanded, setExpanded] = useState(false);
  const [copied,   setCopied]   = useState(false);

  const line     = buildCredentialLine(purchase.credentials, purchase.previewUrl);
  const catKey   = (purchase.category || "other").toLowerCase();
  const emoji    = CATEGORY_EMOJI[catKey]  || "📱";
  const label    = CATEGORY_LABEL[catKey]  || purchase.category;

  const handleCopy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(line).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden transition-all">

      {/* Card header — always visible, tap to expand */}
      <button
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-center gap-3 px-4 py-3.5 text-left hover:bg-gray-50 dark:hover:bg-gray-800/50 transition"
      >
        {/* Emoji icon */}
        <div className="w-9 h-9 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center flex-shrink-0 text-lg">
          {emoji}
        </div>

        {/* Title + meta */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900 dark:text-white truncate leading-snug">
            {purchase.title}
          </p>
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            <span className="text-xs text-gray-400 dark:text-gray-500">
              {label}
            </span>
            <span className="text-gray-300 dark:text-gray-700 text-xs">·</span>
            <span className="text-xs text-gray-400 dark:text-gray-500">
              {formatDate(purchase.createdAt)}
            </span>
          </div>
        </div>

        {/* Right side: price + chevron */}
        <div className="flex flex-col items-end gap-1 flex-shrink-0 ml-2">
          <span className="text-sm font-bold text-purple-600 dark:text-purple-400">
            <Currency amount={purchase.priceNgn} />
          </span>
        </div>

        <div className="ml-2 flex-shrink-0">
          {expanded
            ? <ChevronUp   size={16} className="text-gray-400" />
            : <ChevronDown size={16} className="text-gray-400" />
          }
        </div>
      </button>

      {/* Expanded credential area */}
      {expanded && (
        <div className="px-4 pb-4 border-t border-gray-100 dark:border-gray-800">
          <div className="flex items-center justify-between mt-3 mb-2">
            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
              Credential
            </p>
            <button
              onClick={handleCopy}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition ${
                copied
                  ? "bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400"
                  : "bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 hover:bg-purple-100 dark:hover:bg-purple-900/30"
              }`}
            >
              {copied ? <CheckCheck size={13} /> : <Copy size={13} />}
              {copied ? "Copied!" : "Copy"}
            </button>
          </div>

          <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3 border border-gray-200 dark:border-gray-700">
            <p className="font-mono text-xs text-gray-800 dark:text-gray-200 break-all leading-relaxed whitespace-pre-wrap">
              {line || (
                <span className="text-gray-400 italic">No credentials stored — contact support</span>
              )}
            </p>
          </div>

          <p className="text-xs text-gray-400 dark:text-gray-500 mt-2 text-center">
            Order ID: {String(purchase._id).slice(-8).toUpperCase()}
          </p>
        </div>
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function MyOrders() {
  const [purchases,      setPurchases]      = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [search,         setSearch]         = useState("");
  const [activeCategory, setActiveCategory] = useState(null);
  const [showFilter,     setShowFilter]     = useState(false);
  const [copiedAll,      setCopiedAll]      = useState(false);

  const fetchPurchases = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/social/my-purchases");
      if (res.data.success) setPurchases(res.data.purchases || []);
    } catch {
      toast.error("Failed to load orders");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPurchases(); }, [fetchPurchases]);

  // ── Derived category list from purchases ───────────────────────────────────
  const categoryMap = {};
  for (const p of purchases) {
    const cat = (p.category || "other").toLowerCase();
    if (!categoryMap[cat]) categoryMap[cat] = 0;
    categoryMap[cat]++;
  }
  const categories = Object.entries(categoryMap)
    .sort((a, b) => b[1] - a[1])
    .map(([cat, count]) => ({ cat, label: CATEGORY_LABEL[cat] || cat, count }));

  // ── Filter + Search ────────────────────────────────────────────────────────
  const filtered = purchases.filter(p => {
    if (activeCategory && (p.category || "other").toLowerCase() !== activeCategory) return false;
    if (!search.trim()) return true;
    const term = search.toLowerCase();
    return (
      p.title?.toLowerCase().includes(term)    ||
      p.category?.toLowerCase().includes(term) ||
      p.source?.toLowerCase().includes(term)
    );
  });

  // Total spent on filtered
  const totalSpent = filtered.reduce((s, p) => s + (p.priceNgn || 0), 0);

  // Copy all credentials
  const handleCopyAll = () => {
    const allLines = filtered
      .map(p => `[${p.title}]\n${buildCredentialLine(p.credentials, p.previewUrl)}`)
      .join("\n\n---\n\n");
    navigator.clipboard.writeText(allLines).then(() => {
      setCopiedAll(true);
      setTimeout(() => setCopiedAll(false), 2500);
      toast.success(`Copied ${filtered.length} credential${filtered.length !== 1 ? "s" : ""}!`);
    });
  };

  const isFiltered = activeCategory !== null;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 pb-8">

      {/* ── Page header ──────────────────────────────────────────────────────── */}
      <div className="mb-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">My Orders</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              All your purchased logs and accounts
            </p>
          </div>
          <button
            onClick={fetchPurchases}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 text-sm font-medium rounded-lg hover:border-purple-300 dark:hover:border-purple-700 transition"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>
      </div>

      {/* ── Stats strip ──────────────────────────────────────────────────────── */}
      {!loading && purchases.length > 0 && (
        <div className="grid grid-cols-3 gap-3 mb-5">
          <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 px-4 py-3 text-center">
            <p className="text-lg font-bold text-gray-900 dark:text-white">{purchases.length}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Total Orders</p>
          </div>
          <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 px-4 py-3 text-center">
            <p className="text-lg font-bold text-purple-600 dark:text-purple-400">
              {categories.length}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Categories</p>
          </div>
          <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 px-4 py-3 text-center">
            <p className="text-lg font-bold text-gray-900 dark:text-white">
              <Currency amount={purchases.reduce((s, p) => s + (p.priceNgn || 0), 0)} />
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Total Spent</p>
          </div>
        </div>
      )}

      {/* ── Search + Filter row ───────────────────────────────────────────────── */}
      <div className="flex gap-2 mb-4">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search orders..."
            className="w-full pl-9 pr-4 py-2.5 text-sm bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 dark:text-white"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            >
              <X size={14} />
            </button>
          )}
        </div>

        {/* Filter button */}
        <button
          onClick={() => setShowFilter(v => !v)}
          className={`relative flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg border transition flex-shrink-0 ${
            isFiltered
              ? "bg-purple-600 text-white border-purple-600"
              : "bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-700 hover:border-purple-300 dark:hover:border-purple-700"
          }`}
        >
          <Filter size={15} />
          <span className="hidden sm:inline">Filter</span>
          {isFiltered && (
            <span className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-amber-400 rounded-full text-xs font-bold text-gray-900 flex items-center justify-center">
              1
            </span>
          )}
        </button>
      </div>

      {/* ── Inline filter panel (category chips) ─────────────────────────────── */}
      {showFilter && (
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
              Filter by Category
            </p>
            {isFiltered && (
              <button
                onClick={() => setActiveCategory(null)}
                className="text-xs text-purple-600 dark:text-purple-400 hover:underline"
              >
                Clear
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setActiveCategory(null)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${
                activeCategory === null
                  ? "bg-purple-600 text-white"
                  : "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
              }`}
            >
              All ({purchases.length})
            </button>
            {categories.map(({ cat, label, count }) => (
              <button
                key={cat}
                onClick={() => { setActiveCategory(cat); setShowFilter(false); }}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${
                  activeCategory === cat
                    ? "bg-purple-600 text-white"
                    : "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
                }`}
              >
                {CATEGORY_EMOJI[cat] || "📱"} {label} ({count})
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── Active filter + result count + copy all ───────────────────────────── */}
      {!loading && filtered.length > 0 && (
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            {isFiltered && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-xs font-medium rounded-full">
                {CATEGORY_LABEL[activeCategory] || activeCategory}
                <button onClick={() => setActiveCategory(null)}>
                  <X size={11} />
                </button>
              </span>
            )}
            <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
              {filtered.length} order{filtered.length !== 1 ? "s" : ""}
              {isFiltered || search ? <> · <Currency amount={totalSpent} /> total</> : ""}
            </span>
          </div>

          {filtered.length > 1 && (
            <button
              onClick={handleCopyAll}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition ${
                copiedAll
                  ? "bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 border-green-200 dark:border-green-800"
                  : "bg-white dark:bg-gray-900 text-purple-600 dark:text-purple-400 border-purple-200 dark:border-purple-800 hover:bg-purple-50 dark:hover:bg-purple-900/20"
              }`}
            >
              {copiedAll ? <CheckCheck size={13} /> : <Copy size={13} />}
              {copiedAll ? "Copied all!" : `Copy all (${filtered.length})`}
            </button>
          )}
        </div>
      )}

      {/* ── Content ──────────────────────────────────────────────────────────── */}
      {loading ? (
        // Skeleton loader
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-4 animate-pulse">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-gray-200 dark:bg-gray-700 flex-shrink-0" />
                <div className="flex-1">
                  <div className="h-3.5 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-2" />
                  <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/3" />
                </div>
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-16" />
              </div>
            </div>
          ))}
        </div>

      ) : purchases.length === 0 ? (
        // Empty state
        <div className="flex flex-col items-center justify-center py-20 text-center px-6">
          <div className="w-16 h-16 bg-purple-50 dark:bg-purple-900/20 rounded-2xl flex items-center justify-center mb-4">
            <ShoppingBag size={28} className="text-purple-400" />
          </div>
          <h3 className="text-base font-bold text-gray-900 dark:text-white mb-2">No orders yet</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed max-w-xs">
            When you purchase logs or accounts from the Market Place, they will all appear here.
          </p>
        </div>

      ) : filtered.length === 0 ? (
        // No results for current filter/search
        <div className="flex flex-col items-center justify-center py-16 text-center px-6">
          <Package size={36} className="text-gray-300 dark:text-gray-600 mb-3" />
          <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">No results found</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
            {search ? `Nothing matched "${search}"` : "No orders in this category"}
          </p>
          <button
            onClick={() => { setSearch(""); setActiveCategory(null); }}
            className="px-4 py-2 text-sm font-medium text-purple-600 dark:text-purple-400 border border-purple-200 dark:border-purple-800 rounded-lg hover:bg-purple-50 dark:hover:bg-purple-900/20 transition"
          >
            Clear filters
          </button>
        </div>

      ) : (
        // Orders list
        <div className="space-y-2">
          {filtered.map((purchase, index) => (
            <OrderCard key={purchase._id || index} purchase={purchase} index={index} />
          ))}
        </div>
      )}
    </div>
  );
}